import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faCheckCircle,
  faClock,
  faSpinner,
  faUser,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

/** Minimum characters before any suggestion is revealed. */
const MIN_QUERY_LENGTH = 3;
/** Maximum suggestions rendered at once. */
const MAX_SUGGESTIONS = 8;

const EVENT_IDS = ["JMM", "SMM", "BMM"];

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const normalize = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/**
 * Only fields that a participant is already publicly listed with on the entry
 * list are kept here. Personal data (date of birth, blood type, address,
 * jersey size, phone, email, emergency contact) is deliberately NOT read into
 * the page, so it can never be rendered or inspected from this route.
 */
const mapParticipant = (docSnap, eventId) => {
  const data = docSnap.data();
  const isTeam = data.teamName && data.kategori === "Mix Group (20K)";
  return {
    id: `${eventId}-${docSnap.id}`,
    eventId,
    nama: isTeam
      ? data.teamName
      : data.namaLengkap ||
        [data.namaDepan, data.namaBelakang].filter(Boolean).join(" ") ||
        "Name not available",
    isTeam: Boolean(isTeam),
    bibNumber: data.bibNumber || "",
    gender: data.gender || "-",
    kategori: data.kategori || "-",
    distance: data.distance ?? null,
    kota: data.kota || "-",
    country: data.negara || data.country || data.nationality || "-",
    statusPembayaran: data.transaction_status || "pending",
  };
};

const SuggestionSkeleton = () => (
  <div className="px-4 py-3 flex items-center gap-3">
    <div className="h-4 w-4 rounded-full bg-gray-200 animate-pulse" />
    <div className="h-4 flex-1 rounded bg-gray-200 animate-pulse" />
  </div>
);

const DetailRow = ({ label, value }) => (
  <tr className="border-b border-gray-200 last:border-b-0">
    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-2/5 align-top">
      {label}
    </th>
    <td className="px-4 py-3 text-sm text-slate-800 md:text-left">
      {value || "-"}
    </td>
  </tr>
);

export const CheckYourName = React.memo(() => {
  const [eventTitles, setEventTitles] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [participants, setParticipants] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isMounted = useRef(true);
  const containerRef = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Close the suggestion dropdown when clicking outside of the search box.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Event titles for labelling which event a result belongs to.
  useEffect(() => {
    const fetchTitles = async () => {
      try {
        const snapshot = await getDocs(collection(db, "semuaKegiatan"));
        if (!isMounted.current) return;
        const titles = {};
        snapshot.docs.forEach((eventDoc) => {
          titles[String(eventDoc.id).toUpperCase()] =
            eventDoc.data()?.title || eventDoc.id;
        });
        setEventTitles(titles);
      } catch (err) {
        console.error(`[ERROR] CheckYourName.fetchTitles: ${err.message}`);
      }
    };
    fetchTitles();
  }, []);

  const resolveCollectionName = useCallback(async (eventKey) => {
    try {
      const eventsDoc = await getDoc(doc(db, "events", eventKey));
      if (eventsDoc.exists() && eventsDoc.data()?.collectionName) {
        return eventsDoc.data().collectionName;
      }
    } catch (err) {
      console.error(
        `[ERROR] CheckYourName.resolveCollectionName(${eventKey}): ${err.message}`
      );
    }
    return `pendaftaran${eventKey}`;
  }, []);

  // Load every event's name index up front so one search covers the trilogy.
  useEffect(() => {
    let cancelled = false;

    const fetchAllEvents = async () => {
      setLoadingIndex(true);
      setError(null);

      const results = await Promise.allSettled(
        EVENT_IDS.map(async (eventKey) => {
          const collectionName = await resolveCollectionName(eventKey);
          const snapshot = await getDocs(
            query(
              collection(db, collectionName),
              where("transaction_status", "in", ["settlement", "pending"])
            )
          );
          return snapshot.docs.map((docSnap) =>
            mapParticipant(docSnap, eventKey)
          );
        })
      );

      if (cancelled || !isMounted.current) return;

      const merged = [];
      const failedEvents = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          merged.push(...result.value);
        } else {
          failedEvents.push(EVENT_IDS[index]);
          console.error(
            `[ERROR] CheckYourName.fetchAllEvents(${EVENT_IDS[index]}): ${result.reason?.message}`
          );
        }
      });

      setParticipants(merged);

      if (failedEvents.length === EVENT_IDS.length) {
        setError(
          "Failed to load participant data. Please try again or contact admin."
        );
      } else if (failedEvents.length > 0) {
        setError(
          `Could not load data for ${failedEvents.join(
            ", "
          )}. Results from other events are still shown.`
        );
      }

      setLoadingIndex(false);
    };

    fetchAllEvents();
    return () => {
      cancelled = true;
    };
  }, [resolveCollectionName]);

  // Suggestions are intentionally withheld until the query is specific enough,
  // so the page never exposes the full entry list.
  const suggestions = useMemo(() => {
    const term = normalize(debouncedSearchTerm);
    if (term.length < MIN_QUERY_LENGTH) return [];

    const startsWith = [];
    const contains = [];
    participants.forEach((participant) => {
      const nama = normalize(participant.nama);
      if (!nama) return;
      if (nama.startsWith(term)) startsWith.push(participant);
      else if (nama.includes(term)) contains.push(participant);
    });

    return [...startsWith, ...contains].slice(0, MAX_SUGGESTIONS);
  }, [debouncedSearchTerm, participants]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value || "");
    setSelected(null);
    setSuggestionsOpen(true);
  }, []);

  const handleSelect = useCallback((participant) => {
    setSelected(participant);
    setSearchTerm(participant.nama);
    setSuggestionsOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleClear = useCallback(() => {
    setSearchTerm("");
    setSelected(null);
    setSuggestionsOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!suggestionsOpen || suggestions.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(
          (prev) => (prev - 1 + suggestions.length) % suggestions.length
        );
      } else if (e.key === "Enter") {
        if (activeIndex >= 0) {
          e.preventDefault();
          handleSelect(suggestions[activeIndex]);
        }
      } else if (e.key === "Escape") {
        setSuggestionsOpen(false);
      }
    },
    [suggestionsOpen, suggestions, activeIndex, handleSelect]
  );

  const trimmedTerm = normalize(debouncedSearchTerm);
  const showDropdown =
    suggestionsOpen && !selected && trimmedTerm.length >= MIN_QUERY_LENGTH;
  const noMatch =
    showDropdown && !loadingIndex && suggestions.length === 0 && !error;

  return (
    <section className="w-full bg-white pt-32 pb-20">
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-800 mb-6">
          Check Your Name
        </h1>

        <motion.div
          className="w-full"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <div className="relative w-full" ref={containerRef}>
            <input
              type="text"
              placeholder="Type your name..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setSuggestionsOpen(true)}
              onKeyDown={handleKeyDown}
              className="input w-full p-2 pl-10 pr-10 h-[42px] border border-gray-300 rounded-xl text-slate-800 text-sm"
              aria-label="Search your name"
              aria-autocomplete="list"
              aria-expanded={showDropdown}
              autoComplete="off"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-800"
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}

            <AnimatePresence>
              {showDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden z-30 text-left"
                >
                  {loadingIndex ? (
                    Array.from({ length: 3 }).map((_, index) => (
                      <SuggestionSkeleton key={index} />
                    ))
                  ) : noMatch ? (
                    <p className="px-4 py-3 text-sm text-gray-600">
                      No participant matches that name.
                    </p>
                  ) : (
                    <ul role="listbox">
                      {suggestions.map((participant, index) => (
                        <li key={participant.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={index === activeIndex}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => handleSelect(participant)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                              index === activeIndex
                                ? "bg-slate-100"
                                : "bg-white hover:bg-slate-50"
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={faUser}
                              className="text-gray-400 w-4 h-4 flex-none"
                            />
                            <span className="text-sm text-slate-800 uppercase flex-1 truncate">
                              {participant.nama}
                            </span>
                            <span className="text-xs font-medium text-gray-500 flex-none">
                              {participant.eventId}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {trimmedTerm.length > 0 &&
            trimmedTerm.length < MIN_QUERY_LENGTH &&
            !selected && (
              <p className="mt-2 text-xs text-gray-500">
                Keep typing — at least {MIN_QUERY_LENGTH} characters are needed.
              </p>
            )}

          {loadingIndex && (
            <p className="mt-3 text-xs text-gray-500 flex items-center justify-center gap-2">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              Preparing participant data...
            </p>
          )}

          {error && (
            <p className="mt-4 text-sm text-red-600">{error}</p>
          )}

          <AnimatePresence>
            {selected && (
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="mt-6 bg-white rounded-2xl border border-gray-200 text-left overflow-hidden"
              >
                <div className="bg-gray-100 px-4 py-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                      {selected.isTeam ? "Team" : "Participant"}
                    </p>
                    <p className="text-base md:text-lg font-black uppercase text-slate-800">
                      {selected.nama}
                    </p>
                  </div>
                  {selected.statusPembayaran === "settlement" ? (
                    <span className="flex items-center gap-2 text-sm text-green-600 whitespace-nowrap">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Paid
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-sm text-yellow-600 whitespace-nowrap">
                      <FontAwesomeIcon icon={faClock} />
                      Pending
                    </span>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <tbody>
                      <DetailRow
                        label="Event"
                        value={eventTitles[selected.eventId] || selected.eventId}
                      />
                      <DetailRow label="Category" value={selected.kategori} />
                      <DetailRow
                        label="Distance"
                        value={
                          selected.distance != null
                            ? `${selected.distance} KM`
                            : "-"
                        }
                      />
                      {selected.bibNumber && (
                        <DetailRow
                          label="BIB Number"
                          value={selected.bibNumber}
                        />
                      )}
                      <DetailRow label="Gender" value={selected.gender} />
                      <DetailRow label="City" value={selected.kota} />
                      <DetailRow label="Country" value={selected.country} />
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
});

CheckYourName.displayName = "CheckYourName";
